import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { removeItemToApp } from './AppService';
import { getItemsByApp } from '../Items/ItemService';
import AttachAppItem from './AttachAppItem';
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Container,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { notify } from 'utils/notify';
import { extractErrorMessage } from 'utils/extractErrorMessage';

const AppItemList = () => {
  const theme = useTheme();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);
  const { appid } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    listAppItems();
  }, [appid]);

  const listAppItems = () => {
    setLoading(true);
    getItemsByApp(appid)
      .then((response) => {
        setItems(response.data);
      })
      .catch((error) => {
        console.error(error);
        notify(extractErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const selectItem = (itemid) => {
    console.log(itemid);
    navigate(`/system/dashboard/item/${itemid}`);
  };

  const handleDeleteConfirmation = (itemid) => {
    setItemIdToDelete(itemid);
    setOpenDialog(true);
  };

  const handleDeleteItem = () => {
    removeItem(appid, itemIdToDelete);
    setOpenDialog(false);
  };

  const removeItem = (appid, itemid) => {
    removeItemToApp(appid, itemid)
      .then(() => {
        listAppItems();
      })
      .catch((error) => {
        if (error.response) {
          console.error(
            'Request failed with status code:',
            error.response.status,
          );
          if (
            error.response.status === 400 &&
            typeof error.response.data === 'string' &&
            error.response.data.includes('foreign key constraint')
          ) {
            notify(
              'Cannot delete the item because it is referenced by other records.',
            );
          } else if (error.response.status === 401) {
            notify(
              'Cannot delete or update a parent row: a foreign key constraint fails.',
            );
          } else {
            notify(
              'An error occurred while deleting the item. Please try again later.',
            );
          }
        } else if (error.request) {
          console.error(
            'Request made but no response received:',
            error.request,
          );
        } else {
          console.error('Error setting up the request:', error.message);
        }
      });
  };

  return (
    <Container>
      {loading && <p>Loading...</p>}
      <AttachAppItem listAppItems={listAppItems} appId={appid} />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 750 }} aria-label="simple table">
          <TableHead sx={{ bgcolor: 'alternate.main' }}>
            <TableRow>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  ID
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Item Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Version Number
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Build Number
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  fontWeight={700}
                  sx={{ textTransform: 'uppercase' }}
                >
                  Status
                </Typography>
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
              <DialogTitle>Confirm Deletion</DialogTitle>
              <DialogContent>
                Are you sure you want to remove this item?
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button onClick={handleDeleteItem} autoFocus>
                  Remove
                </Button>
              </DialogActions>
            </Dialog>
            {items.map((item, i) => (
              <TableRow
                key={i}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                onClick={() => selectItem(item.id)}
              >
                <TableCell component="th" scope="row">
                  <ListItem sx={{ p: 0, m: 0 }}>
                    <ListItemAvatar>{/* <Avatar src={} /> */}</ListItemAvatar>
                    <ListItemText primary={item.id} secondary={item.id} />
                  </ListItem>
                </TableCell>
                <TableCell>
                  <Typography>{item.name}</Typography>
                  <Typography color={'text.secondary'} variant={'subtitle2'}>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant={'caption'}
                    fontWeight={700}
                    sx={{
                      bgcolor: alpha(theme.palette.success.light, 0.1),
                      color: theme.palette.success.dark,
                      paddingX: 1.5,
                      paddingY: 0.5,
                      borderRadius: 4,
                      display: 'inline',
                    }}
                  >
                    {item.versionNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={'text.secondary'} variant={'subtitle2'}>
                    {item.buildNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={'text.secondary'} variant={'subtitle2'}>
                    {item.status}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    color={'primary'}
                    variant={'subtitle2'}
                    fontWeight={700}
                    sx={{ cursor: 'pointer' }}
                  >
                    Edit
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    color={'primary'}
                    variant={'subtitle2'}
                    fontWeight={700}
                    sx={{ cursor: 'pointer' }}
                  >
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation(); // Stop the click event
                        handleDeleteConfirmation(item.id); // Show confirmation dialog before deleting
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AppItemList;
